import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Divider,
  Link,
  IconButton,
  InputAdornment,
  Alert,
  Snackbar,
  Grid,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  MenuItem
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import GoogleIcon from '@mui/icons-material/Google';
import FacebookIcon from '@mui/icons-material/Facebook';
import GitHubIcon from '@mui/icons-material/GitHub';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

const steps = ['Account Details', 'Personal Information', 'Complete Registration'];

function Register() {
  const navigate = useNavigate();
  const { register: authRegister } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    company: '',
    jobTitle: '',
    country: '',
    phone: '',
    tradingExperience: '',
    interests: [],
    agreeTerms: false,
    newsletter: true
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateStep = () => {
    let stepValid = true;
    const newErrors = {};
    
    if (activeStep === 0) {
      if (!formData.email) {
        newErrors.email = 'Email is required';
        stepValid = false;
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email is invalid';
        stepValid = false;
      }
      
      if (!formData.password) {
        newErrors.password = 'Password is required';
        stepValid = false;
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
        stepValid = false;
      }
      
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
        stepValid = false;
      }
    } else if (activeStep === 1) {
      if (!formData.firstName) {
        newErrors.firstName = 'First name is required';
        stepValid = false;
      }
      
      if (!formData.lastName) {
        newErrors.lastName = 'Last name is required';
        stepValid = false;
      }
      
      if (!formData.country) {
        newErrors.country = 'Country is required';
        stepValid = false;
      }
    } else if (activeStep === 2) {
      if (!formData.agreeTerms) {
        newErrors.agreeTerms = 'You must agree to the terms and conditions';
        stepValid = false;
      }
    }
    
    setErrors(newErrors);
    return stepValid;
  };

  const handleNext = () => {
    if (validateStep()) {
      if (activeStep === steps.length - 1) {
        handleSubmit();
      } else {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const displayName = [formData.firstName, formData.lastName].filter(Boolean).join(' ') || undefined;
      const result = await authRegister(formData.email, formData.password, displayName);
      if (result.success) {
        setSnackbarMessage('Registration successful! Redirecting...');
        setOpenSnackbar(true);
        setTimeout(() => {
          navigate('/user/dashboard');
        }, 1000);
      } else {
        setSnackbarMessage(result.error || 'Registration failed. Please try again.');
        setOpenSnackbar(true);
      }
    } catch (error) {
      setSnackbarMessage('Registration failed. Please try again.');
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialRegister = (provider) => {
    setIsLoading(true);
    // Simulate OAuth registration
    setTimeout(() => {
      setSnackbarMessage(`Registration with ${provider} successful! Redirecting...`);
      setOpenSnackbar(true);
      
      setTimeout(() => {
        navigate('/user/dashboard');
      }, 2000);
      
      setIsLoading(false);
    }, 1500);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  // Content for each step
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleInputChange}
              error={!!errors.email}
              helperText={errors.email}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              value={formData.password}
              onChange={handleInputChange}
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
            />
          </>
        );
      case 1:
        return (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="firstName"
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="lastName"
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="company"
                  label="Company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="jobTitle"
                  label="Job Title"
                  name="jobTitle"
                  value={formData.jobTitle}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="country"
                  select
                  label="Country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  error={!!errors.country}
                  helperText={errors.country}
                >
                  {['United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France', 'Japan', 'China', 'India', 'Brazil', 'Other'].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="phone"
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
            <TextField
              margin="normal"
              fullWidth
              id="tradingExperience"
              select
              label="Trading Experience"
              name="tradingExperience"
              value={formData.tradingExperience}
              onChange={handleInputChange}
            >
              {['Beginner', 'Intermediate', 'Advanced', 'Professional'].map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </>
        );
      case 2:
        return (
          <>
            <Typography variant="h6" gutterBottom>
              Complete Your Registration
            </Typography>
            <Typography variant="body2" paragraph>
              You're almost done! Please review your information and complete the registration process.
            </Typography>
            
            <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, mb: 2 }}>
              <Typography variant="body2">
                <strong>Email:</strong> {formData.email}
              </Typography>
              <Typography variant="body2">
                <strong>Name:</strong> {formData.firstName} {formData.lastName}
              </Typography>
              {formData.company && (
                <Typography variant="body2">
                  <strong>Company:</strong> {formData.company}
                </Typography>
              )}
              {formData.jobTitle && (
                <Typography variant="body2">
                  <strong>Job Title:</strong> {formData.jobTitle}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Country:</strong> {formData.country}
              </Typography>
              {formData.tradingExperience && (
                <Typography variant="body2">
                  <strong>Trading Experience:</strong> {formData.tradingExperience}
                </Typography>
              )}
            </Box>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.agreeTerms}
                  onChange={handleInputChange}
                  name="agreeTerms"
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the <Link href="#" target="_blank">Terms of Service</Link> and <Link href="#" target="_blank">Privacy Policy</Link>
                </Typography>
              }
            />
            {errors.agreeTerms && (
              <Typography variant="caption" color="error">
                {errors.agreeTerms}
              </Typography>
            )}
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.newsletter}
                  onChange={handleInputChange}
                  name="newsletter"
                  color="primary"
                />
              }
              label="I would like to receive newsletter and special offers"
            />
          </>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        py: 6,
        px: 2
      }}
    >
      <Paper
        elevation={6}
        sx={{
          p: 4,
          maxWidth: 700,
          width: '100%',
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Box
            component="img"
            src="/assets/logo-animated.svg"
            alt="AITrader.gold"
            sx={{ height: 48, mb: 3 }}
          />
          <Typography component="h1" variant="h5" gutterBottom>
            Create Your Account
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            Join AITrader.gold and start optimizing your trading strategies
          </Typography>

          {/* Social Sign Up Buttons */}
          {activeStep === 0 && (
            <>
              <Grid container spacing={2} sx={{ mt: 3, mb: 2 }}>
                <Grid item xs={4}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<GoogleIcon />}
                    onClick={() => handleSocialRegister('Google')}
                    sx={{ borderRadius: 2 }}
                  >
                    Google
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<FacebookIcon />}
                    onClick={() => handleSocialRegister('Facebook')}
                    sx={{ borderRadius: 2 }}
                  >
                    Facebook
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<GitHubIcon />}
                    onClick={() => handleSocialRegister('GitHub')}
                    sx={{ borderRadius: 2 }}
                  >
                    GitHub
                  </Button>
                </Grid>
              </Grid>
              <Divider sx={{ width: '100%', my: 2 }}>OR</Divider>
            </>
          )}

          {/* Stepper */}
          <Stepper activeStep={activeStep} alternativeLabel sx={{ width: '100%', mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ width: '100%' }}>
            {getStepContent(activeStep)}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={isLoading}
              >
                {activeStep === steps.length - 1 ? 'Complete Registration' : 'Next'}
              </Button>
            </Box>
          </Box>

          {activeStep === 0 && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2">
                Already have an account?{' '}
                <Link href="/auth/login" variant="body2">
                  Sign in instead
                </Link>
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarMessage.includes('successful') ? 'success' : 'error'} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Register;
